import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, switchMap, of, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { catchError } from 'rxjs/operators';
import {
  isPlatformAdminUser,
  isGemeindeAdminUser,
  isVereinAdminUser,
  isCompanyAdminUser,
  isShopAdminUser,
  canAccessAdminUser,
} from './auth.roles';

interface VerifyOtpResponse {
  userId?: string;
  name?: string;
}

export interface OrtschaftCheck {
  allowed: boolean;
  message: string | null;
}

export interface AuthUser {
  id: string;
  name: string;
  nickname?: string;
  displayName?: string;
  phone: string;
  address?: string;
  role?: string;
  isGemeindeAdmin?: boolean;
  adminGemeindeIds?: string[];
  isVereinAdmin?: boolean;
  isCompanyAdmin?: boolean;
  adminVereinIds?: string[];
  adminCompanyIds?: string[];
  isShopAdmin?: boolean;
  adminShopIds?: string[];
  skills?: string[];
  lat?: number | null;
  lng?: number | null;
}

const USER_KEY = 'cuminanza_user';

const PROFILE_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private readonly _user = signal<AuthUser | null>(null);
  private pendingPhone: string | null = null;
  private lastProfileRefresh = 0;

  readonly user = this._user.asReadonly();
  readonly isLoggedIn = computed(() => this._user() !== null);
  readonly isPlatformAdmin = computed(() => isPlatformAdminUser(this._user()));
  readonly isGemeindeAdmin = computed(() => isGemeindeAdminUser(this._user()));
  readonly isVereinAdmin = computed(() => isVereinAdminUser(this._user()));
  readonly isCompanyAdmin = computed(() => isCompanyAdminUser(this._user()));
  readonly isShopAdmin = computed(() => isShopAdminUser(this._user()));
  readonly canAccessAdmin = computed(() => canAccessAdminUser(this._user()));
  readonly adminGemeindeIds = computed(() => this._user()?.adminGemeindeIds ?? []);

  constructor() {
    const stored = localStorage.getItem(USER_KEY);
    if (stored) {
      this._user.set(JSON.parse(stored));
      this.loadProfile().subscribe();
    }
  }

  refreshProfileIfStale(): void {
    const now = Date.now();
    if (now - this.lastProfileRefresh < PROFILE_REFRESH_INTERVAL_MS) return;
    this.lastProfileRefresh = now;
    this.loadProfile().subscribe();
  }

  loadProfile(): Observable<AuthUser | null> {
    this.lastProfileRefresh = Date.now();
    return this.http
      .get<{
        id?: string;
        lat?: number;
        lng?: number;
        nickname?: string;
        displayName?: string;
        role?: string;
        isGemeindeAdmin?: boolean;
        adminGemeindeIds?: string[];
        isVereinAdmin?: boolean;
        isCompanyAdmin?: boolean;
        adminVereinIds?: string[];
        adminCompanyIds?: string[];
        isShopAdmin?: boolean;
        adminShopIds?: string[];
        skills?: string[];
      }>(`${environment.apiUrl}/api/auth/me`)
      .pipe(
        tap((profile) => {
          const u = this._user();
          if (u) {
            const updated = {
              ...u,
              id: profile.id ?? u.id,
              lat: profile.lat,
              lng: profile.lng,
              nickname: profile.nickname,
              displayName: profile.displayName,
              role: profile.role,
              isGemeindeAdmin: profile.isGemeindeAdmin ?? false,
              adminGemeindeIds: profile.adminGemeindeIds || [],
              isVereinAdmin: profile.isVereinAdmin,
              isCompanyAdmin: profile.isCompanyAdmin,
              adminVereinIds: profile.adminVereinIds || [],
              adminCompanyIds: profile.adminCompanyIds || [],
              isShopAdmin: profile.isShopAdmin ?? false,
              adminShopIds: profile.adminShopIds || [],
              skills: profile.skills,
            };
            localStorage.setItem(USER_KEY, JSON.stringify(updated));
            this._user.set(updated);
          }
        }),
        switchMap(() => of(this._user())),
        catchError(() => of(this._user())),
      );
  }

  requestOtp(phone: string): Observable<{ phone: string }> {
    return this.http
      .post<{
        message: string;
        phone: string;
      }>(`${environment.apiUrl}/api/auth/request-otp`, { phone })
      .pipe(tap((res) => (this.pendingPhone = res.phone)));
  }

  serviceAreaGemeinden(): Observable<string[]> {
    return this.http
      .get<{ gemeinden: string[] }>(`${environment.apiUrl}/api/auth/service-area`)
      .pipe(
        map((response) => response.gemeinden ?? []),
        catchError(() => of([])),
      );
  }

  checkOrtschaft(plz: string): Observable<OrtschaftCheck> {
    return this.http
      .get<OrtschaftCheck>(`${environment.apiUrl}/api/auth/ortschaft-check`, { params: { plz } })
      .pipe(catchError(() => of({ allowed: true, message: null })));
  }

  verifyOtp(code: string): Observable<AuthUser | null> {
    return this.http
      .post<VerifyOtpResponse>(`${environment.apiUrl}/api/auth/verify-otp`, {
        phone: this.pendingPhone,
        code,
      })
      .pipe(
        tap((res) => {
          if (res.userId)
            this.saveSession({ id: res.userId, name: res.name ?? '', phone: this.pendingPhone! });
        }),
        switchMap((res) => (res.userId ? this.loadProfile() : of(null))),
      );
  }

  register(data: {
    name: string;
    nickname?: string;
    address?: string;
    plz: string;
    lat?: number | null;
    lng?: number | null;
    termsAccepted?: boolean;
    dateOfBirth: string;
  }): Observable<AuthUser> {
    return this.http
      .post<{
        token: string;
        userId: string;
        name: string;
      }>(`${environment.apiUrl}/api/auth/register`, {
        phone: this.pendingPhone,
        name: data.name,
        nickname: data.nickname,
        address: data.address,
        plz: data.plz,
        lat: data.lat,
        lng: data.lng,
        termsAccepted: data.termsAccepted ?? false,
        dateOfBirth: data.dateOfBirth,
      })
      .pipe(
        tap((res) =>
          this.saveSession({ id: res.userId, name: res.name, phone: this.pendingPhone! }),
        ),
        switchMap(() => this.loadProfile()),
        switchMap(() => of(this._user()!)),
      );
  }

  private saveSession(user: AuthUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this._user.set(user);
    this.pendingPhone = null;
  }

  getToken(): string | null {
    return null;
  }

  getUser(): AuthUser | null {
    return this._user();
  }

  getPendingPhone(): string | null {
    return this.pendingPhone;
  }

  setPendingPhone(phone: string): void {
    this.pendingPhone = phone;
  }

  checkPhone(phone: string): Observable<{ status: 'not_found' | 'unverified' | 'verified' }> {
    return this.http.get<{ status: 'not_found' | 'unverified' | 'verified' }>(
      `${environment.apiUrl}/api/auth/check-phone`,
      { params: { phone } },
    );
  }

  requestDeletion(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/api/auth/deletion-request`, {});
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/api/auth/logout`, {}).subscribe();
    localStorage.removeItem(USER_KEY);
    this._user.set(null);
  }
}
